// src/components/calculator/SettlementForm.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  HStack,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  Grid,
  Separator,
  SimpleGrid,
  NativeSelect,
  IconButton,
  Checkbox,
} from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import useExpenseData from "../../../utils/useExpenseData";
import { computeSettlement } from "../../../utils/setttlementEngine"; // <-- fix path
import prospectContext from "../../../context/prospect/prospectContext";

// ----- helpers -----
const yearRange = (start = 2010) => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let y = start; y <= now; y++) arr.push(y);
  return arr;
};

export default function SettlementForm({
  setSettlementCalculation,
  clearSettlementCalculation,
}) {
  const { createProspect, creating } = useContext(prospectContext);

  const { housing, auto, healthCareExpenses, livingExpenses, states } =
    useExpenseData();
  const cardBg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };
  const subtle = { base: "gray.600", _dark: "gray.300" };

  // ---- Local error state
  const [error, setError] = useState("");

  // ---- Draft inputs for year rows
  const [draftProblemYear, setDraftProblemYear] = useState("");
  const [draftIncomeType, setDraftIncomeType] = React.useState("");
  const [draftIncomeAmount, setDraftIncomeAmount] = React.useState("");
  const [draftLiabType, setDraftLiabType] = React.useState("");
  const [draftTotal, setDraftTotal] = React.useState("");
  const [draftPayment, setDraftPayment] = React.useState("");
  const [draftAvailCredit, setDraftAvailCredit] = React.useState("");
  const [draftAssetType, setDraftAssetType] = React.useState("");
  const [draftAssetValue, setDraftAssetValue] = React.useState("");
  const [draftAssetLiab, setDraftAssetLiab] = React.useState("");

  const addAssetFromDraft = () => {
    const type = draftAssetType.trim();
    const value = Number(draftAssetValue || 0);
    const liab = Number(draftAssetLiab || 0);
    if (!type) return;

    // your existing setter, adapt if form is in context
    setForm((f) => ({
      ...f,
      assets: [
        ...(f.assets || []),
        { assetType: type, value, assetLiab: liab },
      ],
    }));

    setDraftAssetType("");
    setDraftAssetValue("");
    setDraftAssetLiab("");
  };

  // ---- Lists used by selects
  const years = useMemo(() => yearRange(2010), []);
  const stateOptions = Array.isArray(states) ? states : [];
  const defaultForm = {
    totalBalance: 0,
    name: "",
    email: "",
    phone: "",
    problemYears: [],
    incomes: [], // [{ incomeType: "", amount: 0 }]
    expenseData: { state: "", cars: 0, residents: 0, residents65: 0 },
    otherLiabilities: [], // [{ liabType: "", total: 0, payment: 0, availCredit: 0 }]
    assets: [], // [{ assetType: "", value: 0, assetLiab: 0 }]
  };

  const [form, setForm] = useState(defaultForm);

  const clearForm = () => {
    setForm(defaultForm);
    clearSettlementCalculation();
  };

  const safeNum = (v) => {
    if (typeof v === "string") {
      const s = v.replace(/,/g, "");
      // Preserve blank input so controlled <Input type="number"> doesn't jump to 0
      if (s.trim() === "") return "";
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    }
    return Number.isFinite(v) ? v : 0;
  };
  const toNum = (v) => (v === "" || v == null ? 0 : Number(v));

  function addDraftOtherLiab() {
    if (!draftLiabType) return; // require a type at least

    // push into your form using your existing setter/util
    addOtherLiab({
      liabType: draftLiabType,
      total: toNum(draftTotal),
      payment: toNum(draftPayment),
      availCredit:
        draftLiabType === "Credit Card" ? toNum(draftAvailCredit) : 0,
    });

    // reset draft
    setDraftLiabType("");
    setDraftTotal("");
    setDraftPayment("");
    setDraftAvailCredit("");
  }
  // ----- Total Balance (tax liability) onChange -----
  const onChangeSurface = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      // coerce numeric if totalBalance present
      if (patch.hasOwnProperty("totalBalance")) {
        next.totalBalance = safeNum(patch.totalBalance);
      }
      return next;
    });
  };

  // ----- Expense Data onChange (partial patch) -----
  const onChangeExpenseData = (patch) => {
    setForm((prev) => ({
      ...prev,
      expenseData: { ...prev.expenseData, ...patch },
    }));
  };

  // ========================= Unfiled Years =========================
  // add: push {year:0, income:0} or custom init

  // ========================= Incomes =========================
  // local draft inputs

  // util for formatting
  const dollars = (n) =>
    Number(n ?? 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  // add income row
  function addIncome() {
    const amt = Number(draftIncomeAmount);

    if (!draftIncomeType || !Number.isFinite(amt) || amt <= 0) return;

    setForm((prev) => {
      const next = Array.isArray(prev.incomes) ? prev.incomes.slice() : [];
      next.push({ incomeType: draftIncomeType, amount: amt });
      return { ...prev, incomes: next };
    });
    setDraftIncomeType("");
    setDraftIncomeAmount("");
  }

  // remove income row by index
  function delIncome(idx) {
    setForm((prev) => {
      const next = (prev.incomes || []).slice();
      next.splice(idx, 1);
      return { ...prev, incomes: next };
    });
  }

  // ========================= Other Liabilities =========================
  const addOtherLiab = (
    init = { liabType: "", total: 0, payment: 0, availCredit: 0 }
  ) => {
    setForm((prev) => ({
      ...prev,
      otherLiabilities: [
        ...prev.otherLiabilities,
        {
          liabType: init.liabType ?? "",
          total: safeNum(init.total),
          payment: safeNum(init.payment),
          availCredit: safeNum(init.availCredit),
        },
      ],
    }));
  };

  const delOtherLiab = (index) => {
    setForm((prev) => {
      const next = prev.otherLiabilities.slice();
      next.splice(index, 1);
      return { ...prev, otherLiabilities: next };
    });
  };

  // ========================= Assets =========================

  const delAsset = (index) => {
    setForm((prev) => {
      const next = prev.assets.slice();
      next.splice(index, 1);
      return { ...prev, assets: next };
    });
  };

  // ---- Submit
  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      // 1) Save raw inputs to your backend (prospect pipeline)
      await createProspect(form);

      // 2) Compute results from the same raw form
      const results = computeSettlement(form);

      // 3) Store results for SettlementResults.js to render
      setSettlementCalculation(results);
    } catch (err) {
      setError(err?.message || "Could not submit. Please review your entries.");
    }
  };

  console.log(form);
  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      bg={cardBg}
      borderWidth="1px"
      borderColor={border}
      rounded="lg"
      // keep the card narrower than any row inside it
      maxW={{ base: "100%", sm: "580px", md: "680px" }} // ← tighter cap
      w="100%"
      mx="auto" // center it
      px={{ base: 4, md: 5 }} // compact padding
      py={{ base: 4, md: 5 }}
    >
      <Stack
        direction="column" // don’t split columns inside the narrow card
        spacing={6}
        align="stretch"
      >
        {/* --- Column 1: Contact + Tax Debt + Income --- */}
        <VStack spacing={3} align="stretch" flex="1">
          <Heading size="md">Contact Information</Heading>
          <Stack spacing={4}>
            {/* Full Name */}
            <Box w="100%" maxW="540px">
              <Text fontSize="sm" mb={1}>
                Full Name
              </Text>
              <Input
                size="md"
                placeholder="Jane Smith"
                required
                value={form.name}
                onChange={(e) => onChangeSurface({ name: e.target.value })}
              />
            </Box>

            {/* Email */}
            <Box w="100%" maxW="540px">
              <Text fontSize="sm" mb={1}>
                Email
              </Text>
              <Input
                size="md"
                type="email"
                placeholder="jane@example.com"
                required
                value={form.email}
                onChange={(e) => onChangeSurface({ email: e.target.value })}
              />
            </Box>

            {/* Phone */}
            <Box w="100%" maxW="540px">
              <Text fontSize="sm" mb={1}>
                Phone
              </Text>
              <Input
                size="md"
                placeholder="(555) 201-0000"
                required
                value={form.phone}
                onChange={(e) => onChangeSurface({ phone: e.target.value })}
              />
            </Box>
          </Stack>

          <Separator />

          <Heading size="md">Tax Debt</Heading>
          <Stack spacing={4}>
            <Box w="100%" maxW="360px">
              <Text fontSize="sm" mb={1}>
                Balance Due (Total IRS)
              </Text>
              <Input
                size="md"
                type="number"
                min={0}
                placeholder="e.g. 25000"
                value={form.totalBalance}
                onChange={(e) =>
                  onChangeSurface({ totalBalance: e.target.value })
                }
              />
            </Box>
          </Stack>
          {/* Problem Years — compact select + add */}
          <Box>
            <Text fontSize="sm" mb={2}>
              Problem Years
            </Text>

            <HStack spacing={2} align="center" flexWrap="wrap">
              {/* Year select */}
              <NativeSelect.Root size="md" width="160px">
                <NativeSelect.Field
                  value={draftProblemYear}
                  onChange={(e) => setDraftProblemYear(e.target.value)}
                >
                  <option value="">Year</option>
                  {years.map((y) => (
                    <option key={`problem-year-${y}`} value={y}>
                      {y}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>

              <Button
                onClick={() => {
                  if (!draftProblemYear) return;
                  const y = Number(draftProblemYear);
                  if (!Number.isFinite(y)) return;

                  setForm((prev) => {
                    const next = Array.isArray(prev.problemYears)
                      ? prev.problemYears.slice()
                      : [];
                    if (!next.includes(y)) next.push(y);
                    return {
                      ...prev,
                      problemYears: next.sort((a, b) => a - b),
                    };
                  });
                  setDraftProblemYear("");
                }}
                size="sm"
                variant="outline"
                px={3}
              >
                Add
              </Button>
            </HStack>

            {/* Compact list of added years */}
            <Stack mt={2} spacing={2}>
              {(form.problemYears || []).map((y, idx) => (
                <HStack
                  key={`py-${y}-${idx}`}
                  justify="space-between"
                  borderWidth="1px"
                  borderColor={border}
                  rounded="md"
                  px={2}
                  py={1.5}
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {y}
                  </Text>
                  <IconButton
                    aria-label="Remove year"
                    icon={<Trash2 size={16} />}
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setForm((prev) => {
                        const next = (prev.problemYears || []).filter(
                          (v) => v !== y
                        );
                        return { ...prev, problemYears: next };
                      })
                    }
                  />
                </HStack>
              ))}
            </Stack>
          </Box>

          <Separator />
          <Heading size="md">Expenses (Allowances)</Heading>
          <HStack spacing={3} wrap="wrap">
            {/* State */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                State
              </Text>
              <NativeSelect.Root size="sm" variant="flushed" width="140px">
                <NativeSelect.Field
                  value={form.expenseData.state}
                  onChange={(e) =>
                    onChangeExpenseData({ state: e.target.value })
                  }
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {stateOptions.map((s) => (
                    <option key={s.abbreviation} value={s.abbreviation}>
                      {s.abbreviation}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* Cars */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                Cars
              </Text>
              <NativeSelect.Root size="sm" variant="flushed" width="88px">
                <NativeSelect.Field
                  value={form.expenseData.cars}
                  onChange={(e) =>
                    onChangeExpenseData({ cars: safeNum(e.target.value) })
                  }
                >
                  <option value={0}>0</option>
                  <option value={1}>1</option>
                  <option value={2}>2+</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* Residents */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                Residents
              </Text>
              <NativeSelect.Root size="sm" variant="flushed" width="88px">
                <NativeSelect.Field
                  value={form.expenseData.residents}
                  onChange={(e) =>
                    onChangeExpenseData({ residents: safeNum(e.target.value) })
                  }
                >
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* 65+ */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                65+ Residents
              </Text>
              <NativeSelect.Root size="sm" variant="flushed" width="110px">
                <NativeSelect.Field
                  value={form.expenseData.residents65}
                  onChange={(e) =>
                    onChangeExpenseData({
                      residents65: safeNum(e.target.value),
                    })
                  }
                >
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>
          </HStack>

          <Text fontSize="xs" opacity={0.7} mt={2}>
            We use national/local standards for auto, housing, healthcare, and
            living allowances by family size.
          </Text>

          <Separator />
          {/* ====================== Monthly Income (Pre-tax) ====================== */}
          <Heading size="md">Monthly Income (Pre-tax)</Heading>

          {/* Add row — responsive (stacks on mobile, single row on md+) */}
          <Stack
            spacing={3}
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "end" }}
            pb={1}
          >
            {/* Type */}
            <Box
              flex={{ base: "1 1 100%", md: "0 0 260px" }}
              w={{ base: "100%", md: "auto" }}
              minW={0}
            >
              <Text fontSize="sm" mb={1}>
                Type
              </Text>
              <NativeSelect.Root size="md" width="100%">
                <NativeSelect.Field
                  value={draftIncomeType}
                  onChange={(e) => setDraftIncomeType(e.target.value)}
                >
                  <option value="">Select…</option>
                  <option value="salary">Salary & Wages</option>
                  <option value="passive">
                    Disbursements / SSI / Retirement
                  </option>
                  <option value="child">Child Support / Alimony</option>
                  <option value="rental">Rental Properties</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* Amount */}
            <Box
              flex={{ base: "1 1 100%", md: "0 0 180px" }}
              w={{ base: "100%", md: "auto" }}
              minW={0}
            >
              <Text fontSize="sm" mb={1}>
                Amount
              </Text>
              <Input
                size="md"
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                placeholder="e.g. 3500"
                value={draftIncomeAmount}
                onChange={(e) => setDraftIncomeAmount(e.target.value)}
                w="100%"
              />
            </Box>

            {/* Add button */}
            <Button
              onClick={addIncome}
              variant="outline"
              size="sm"
              px={4}
              alignSelf={{ base: "stretch", md: "auto" }}
            >
              Add
            </Button>
          </Stack>

          {/* List of added incomes (compact cards) */}
          <Stack spacing={2}>
            {(form.incomes || []).map((inc, idx) => (
              <HStack
                key={`inc-${idx}`}
                justify="space-between"
                borderWidth="1px"
                borderColor={border}
                rounded="md"
                px={2}
                py={1.5}
                flexWrap="wrap" // allow wrapping on narrow screens
                gap={2}
                align="start"
              >
                <HStack spacing={4} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="semibold">
                    {{
                      salary: "Salary & Wages",
                      passive: "Disbursements / SSI / Retirement",
                      child: "Child Support / Alimony",
                      rental: "Rental Properties",
                    }[inc.incomeType] ||
                      inc.incomeType ||
                      "Income"}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {dollars(inc.amount)}
                  </Text>
                </HStack>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => delIncome(idx)}
                  alignSelf={{ base: "stretch", sm: "center" }}
                >
                  Remove
                </Button>
              </HStack>
            ))}
          </Stack>

          <Heading size="md">Other Liabilities</Heading>

          {/* Add row — single line, compact */}
          {/* ====================== Other Liabilities — Add Row ====================== */}
          <Stack
            spacing={3}
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "end" }}
            pb={1}
          >
            {/* Type */}
            <Box
              flex={{ base: "1 1 100%", md: "0 0 240px" }}
              w={{ base: "100%", md: "auto" }}
              minW={0}
            >
              <Text fontSize="sm" mb={1}>
                Type
              </Text>
              <NativeSelect.Root size="md" width="100%">
                <NativeSelect.Field
                  value={draftLiabType}
                  onChange={(e) => setDraftLiabType(e.target.value)}
                >
                  <option value="">Select…</option>
                  <option value="State Tax Debt">State Tax Debt</option>
                  <option value="Alimony/Child Support">
                    Alimony/Child Support
                  </option>
                  <option value="Student Loans">Student Loans</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Private Lender Loans">
                    Private Lender Loans
                  </option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* Total Liability */}
            <Box
              flex={{ base: "1 1 100%", md: "0 0 180px" }}
              w={{ base: "100%", md: "auto" }}
              minW={0}
            >
              <Text fontSize="sm" mb={1}>
                Total
              </Text>
              <Input
                size="md"
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                placeholder="e.g. 12000"
                value={draftTotal}
                onChange={(e) => setDraftTotal(e.target.value)}
                w="100%"
              />
            </Box>

            {/* Monthly Payment */}
            <Box
              flex={{ base: "1 1 100%", md: "0 0 180px" }}
              w={{ base: "100%", md: "auto" }}
              minW={0}
            >
              <Text fontSize="sm" mb={1}>
                Monthly
              </Text>
              <Input
                size="md"
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                placeholder="e.g. 250"
                value={draftPayment}
                onChange={(e) => setDraftPayment(e.target.value)}
                w="100%"
              />
            </Box>

            {/* Available Credit — only for Credit Card */}
            {draftLiabType === "Credit Card" && (
              <Box
                flex={{ base: "1 1 100%", md: "0 0 200px" }}
                w={{ base: "100%", md: "auto" }}
                minW={0}
              >
                <Text fontSize="sm" mb={1}>
                  Available Credit
                </Text>
                <Input
                  size="md"
                  type="number"
                  min={0}
                  step="1"
                  inputMode="numeric"
                  placeholder="e.g. 5000"
                  value={draftAvailCredit}
                  onChange={(e) => setDraftAvailCredit(e.target.value)}
                  w="100%"
                />
              </Box>
            )}

            {/* Add button */}
            <Button
              variant="outline"
              size="sm"
              px={4}
              onClick={addDraftOtherLiab}
              alignSelf={{ base: "stretch", md: "auto" }}
            >
              Add
            </Button>
          </Stack>

          {/* List of added liabilities */}
          <VStack spacing={2} align="stretch" mt={3}>
            {(form.otherLiabilities || []).map((l, i) => (
              <HStack
                key={`ol-${i}`}
                justify="space-between"
                borderWidth="1px"
                borderColor={border}
                rounded="md"
                px={3}
                py={2}
              >
                <HStack spacing={5} wrap="wrap">
                  <Text fontSize="sm" fontWeight="semibold">
                    {l.liabType || "—"}
                  </Text>
                  <Text fontSize="sm">
                    Total: <b>${Number(l.total || 0).toLocaleString()}</b>
                  </Text>
                  <Text fontSize="sm">
                    Monthly: <b>${Number(l.payment || 0).toLocaleString()}</b>
                  </Text>
                </HStack>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => delOtherLiab(i)}
                >
                  Remove
                </Button>
              </HStack>
            ))}
          </VStack>
          <Heading size="md">Assets</Heading>

          {/* Compact add row */}
          <HStack spacing={3} align="end" wrap="wrap">
            {/* Type */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                Type
              </Text>
              <NativeSelect.Root size="sm" variant="flushed" width="220px">
                <NativeSelect.Field
                  value={draftAssetType}
                  onChange={(e) => setDraftAssetType(e.target.value)}
                >
                  <option value="">Select…</option>
                  <option value="Non-Primary Residence">
                    Non-Primary Residence
                  </option>
                  <option value="Retirement Account">Retirement Account</option>
                  <option value="Savings Account">Savings Account</option>
                  <option value="Stocks and Bonds">Stocks and Bonds</option>
                  <option value="Non-Primary Cars and Trucks">
                    Non-Primary Cars & Trucks
                  </option>
                  <option value="Credit Car">Credit Card</option>
                  <option value="Recreational Vehicles">
                    Recreational Vehicles
                  </option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Box>

            {/* Estimated Value */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                Value
              </Text>
              <Input
                size="sm"
                variant="flushed"
                type="number"
                min={0}
                inputMode="numeric"
                w="140px"
                value={draftAssetValue}
                onChange={(e) => setDraftAssetValue(e.target.value)}
                placeholder="15000"
              />
            </Box>

            {/* Liability on Asset */}
            <Box w="auto">
              <Text fontSize="xs" mb={0.5} opacity={0.8}>
                Liability
              </Text>
              <Input
                size="sm"
                variant="flushed"
                type="number"
                min={0}
                inputMode="numeric"
                w="140px"
                value={draftAssetLiab}
                onChange={(e) => setDraftAssetLiab(e.target.value)}
                placeholder="5000"
              />
            </Box>

            {/* Add button */}
            <Button
              onClick={addAssetFromDraft}
              variant="outline"
              size="sm"
              px={4}
              isDisabled={!draftAssetType || draftAssetValue === ""}
            >
              Add
            </Button>
          </HStack>

          {/* Compact list of added assets */}
          <Stack mt={3} spacing={2}>
            {(form.assets || []).map((a, idx) => {
              const net = Math.max(
                0,
                Number(a.value || 0) - Number(a.assetLiab || 0)
              );
              return (
                <HStack
                  key={`asset-${idx}`}
                  justify="space-between"
                  borderWidth="1px"
                  borderColor={border}
                  rounded="md"
                  px={3}
                  py={2}
                  wrap="wrap"
                  gap={2}
                >
                  <HStack spacing={4} align="center">
                    <Text fontSize="sm" fontWeight="semibold">
                      {a.assetType || "—"}
                    </Text>
                    <Text fontSize="sm">Value: {dollars(a.value)}</Text>
                    <Text fontSize="sm">Liability: {dollars(a.assetLiab)}</Text>
                    <Text fontSize="sm" fontWeight="semibold">
                      Net: {dollars(net)}
                    </Text>
                  </HStack>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => delAsset(idx)}
                  >
                    Remove
                  </Button>
                </HStack>
              );
            })}
          </Stack>
          <Separator />
          <HStack spacing={3} mt={4} wrap="wrap">
            <Button type="submit" colorScheme="blue" size="md" px={5} w="auto">
              Calculate
            </Button>

            <Button
              type="button" // not a submit
              variant="outline"
              size="sm"
              px={4}
              w="auto"
              onClick={clearForm}
            >
              Clear
            </Button>
          </HStack>

          {error ? (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          ) : null}
        </VStack>
      </Stack>
    </Box>
  );
}
